import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, LogOut, Inbox, RefreshCw, FileText, Phone, MapPin,
  Building2, Loader2, ArrowLeft, Search,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nf_admin_token";

const inputCls =
  "w-full h-12 px-4 bg-white border-2 border-charcoal/20 font-body text-charcoal placeholder:text-charcoal/30 focus:border-safety focus:outline-none transition-colors";

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success(`Welcome back, ${data.name}`);
      onLogin(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink noise flex items-center justify-center px-5">
      <div className="absolute top-0 left-0 right-0 h-2 stripes" />
      <div className="w-full max-w-md">
        <a href="/" className="inline-flex items-center gap-2 text-white/50 hover:text-safety font-mono2 text-xs uppercase tracking-widest mb-8" data-testid="admin-back-home">
          <ArrowLeft size={14} /> Back to site
        </a>
        <div className="bg-white border-2 border-white/0 p-8 lg:p-10 hard-shadow-accent">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 stripes border-2 border-charcoal flex items-center justify-center">
              <Lock size={18} className="text-charcoal mix-blend-difference text-white" />
            </div>
            <div>
              <h1 className="font-display font-700 uppercase text-2xl text-charcoal leading-none">Admin Access</h1>
              <p className="font-mono2 text-[10px] tracking-widest text-charcoal/50 mt-1">NIDHI FABRICATION · RFQ INBOX</p>
            </div>
          </div>
          <form onSubmit={submit} data-testid="admin-login-form" className="space-y-5">
            <div>
              <label className="block font-mono2 text-[11px] font-700 uppercase tracking-wider text-charcoal/70 mb-2">Email</label>
              <input data-testid="admin-email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
            </div>
            <div>
              <label className="block font-mono2 text-[11px] font-700 uppercase tracking-wider text-charcoal/70 mb-2">Password</label>
              <input data-testid="admin-password" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            <button type="submit" disabled={loading} data-testid="admin-login-submit"
              className="w-full h-13 py-4 bg-safety text-white font-mono2 text-sm font-700 uppercase tracking-wider flex items-center justify-center gap-2 hover:translate-x-[2px] hover:translate-y-[2px] transition-transform hard-shadow-sm disabled:opacity-70">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />} Sign In
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function InboxView({ token, onLogout }) {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/rfqs`, { headers: { Authorization: `Bearer ${token}` } });
      setRfqs(data.rfqs);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load RFQs");
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const filtered = rfqs.filter((r) => {
    const s = `${r.full_name} ${r.company_name || ""} ${r.phone} ${r.location || ""} ${r.service_type}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-offwhite">
      {/* Top bar */}
      <div className="bg-charcoal text-white sticky top-0 z-40 border-b-2 border-safety">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Inbox size={20} className="text-safety" />
            <span className="font-display font-700 uppercase text-lg tracking-wide">RFQ Inbox</span>
            <span className="font-mono2 text-xs bg-safety text-charcoal px-2 py-0.5" data-testid="rfq-count">{rfqs.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="font-mono2 text-xs uppercase tracking-wider text-white/70 hover:text-safety px-3 hidden sm:block">View Site</a>
            <button onClick={load} data-testid="rfq-refresh" className="flex items-center gap-2 px-3 h-9 border border-white/20 font-mono2 text-xs uppercase hover:border-safety hover:text-safety transition-colors">
              <RefreshCw size={14} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button onClick={onLogout} data-testid="admin-logout" className="flex items-center gap-2 px-3 h-9 bg-safety text-white font-mono2 text-xs font-700 uppercase hover:opacity-90">
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8">
        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input data-testid="rfq-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, phone, service..."
            className="w-full h-11 pl-10 pr-4 bg-white border-2 border-charcoal/20 focus:border-safety focus:outline-none font-body text-sm" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-charcoal/50 gap-3">
            <Loader2 className="animate-spin" /> Loading requests...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 border-2 border-dashed border-charcoal/20" data-testid="rfq-empty">
            <Inbox size={40} className="mx-auto text-charcoal/30 mb-4" />
            <p className="font-display font-700 uppercase text-2xl text-charcoal">No requests yet</p>
            <p className="text-charcoal/50 text-sm mt-2">New RFQ submissions will appear here.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-5">
            {/* List */}
            <div className="lg:col-span-5 space-y-3">
              {filtered.map((r) => (
                <button key={r.id} onClick={() => setActive(r)} data-testid={`rfq-row-${r.id}`}
                  className={`w-full text-left bg-white border-2 p-4 transition-colors ${active?.id === r.id ? "border-safety" : "border-charcoal/15 hover:border-charcoal"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-display font-700 uppercase text-lg text-charcoal leading-none">{r.full_name}</span>
                    <span className="font-mono2 text-[10px] bg-charcoal text-white px-2 py-1 whitespace-nowrap">{r.service_type}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 font-mono2 text-[11px] text-charcoal/60">
                    {r.company_name && <span className="flex items-center gap-1"><Building2 size={12} /> {r.company_name}</span>}
                    <span className="flex items-center gap-1"><Phone size={12} /> {r.phone}</span>
                    {r.location && <span className="flex items-center gap-1"><MapPin size={12} /> {r.location}</span>}
                  </div>
                  <div className="font-mono2 text-[10px] text-charcoal/40 mt-2">{fmtDate(r.created_at)}</div>
                </button>
              ))}
            </div>

            {/* Detail */}
            <div className="lg:col-span-7">
              <div className="sticky top-24 bg-white border-2 border-charcoal p-7 min-h-[300px]" data-testid="rfq-detail">
                {active ? (
                  <>
                    <div className="flex items-start justify-between mb-6 pb-5 border-b-2 border-charcoal/10">
                      <div>
                        <h2 className="font-display font-700 uppercase text-3xl text-charcoal leading-none">{active.full_name}</h2>
                        <p className="font-mono2 text-xs text-charcoal/50 mt-2">{fmtDate(active.created_at)}</p>
                      </div>
                      <span className="font-mono2 text-xs bg-safety text-charcoal px-3 py-1.5 font-700 uppercase">{active.service_type}</span>
                    </div>
                    <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-5 font-body text-sm">
                      {[
                        ["Company", active.company_name],
                        ["Phone", active.phone],
                        ["Location / MIDC", active.location],
                        ["Est. Tonnage / Area", active.estimate],
                      ].map(([k, v]) => (
                        <div key={k}>
                          <dt className="font-mono2 text-[10px] uppercase tracking-widest text-charcoal/40 mb-1">{k}</dt>
                          <dd className="text-charcoal font-600">{v || "—"}</dd>
                        </div>
                      ))}
                    </dl>
                    {active.message && (
                      <div className="mt-6">
                        <div className="font-mono2 text-[10px] uppercase tracking-widest text-charcoal/40 mb-2">Message</div>
                        <p className="text-charcoal/80 text-sm leading-relaxed bg-surfacealt p-4 border-l-2 border-safety">{active.message}</p>
                      </div>
                    )}
                    <div className="mt-6 flex flex-wrap gap-3">
                      {active.drawing_name ? (
                        <a href={`${API}/rfq/${active.id}/drawing`} target="_blank" rel="noopener noreferrer" data-testid="rfq-drawing-link"
                          className="inline-flex items-center gap-2 px-5 h-11 bg-charcoal text-white font-mono2 text-xs font-700 uppercase tracking-wider hover:bg-safety hover:text-charcoal transition-colors">
                          <FileText size={15} /> Open Drawing ({active.drawing_name})
                        </a>
                      ) : (
                        <span className="font-mono2 text-xs text-charcoal/40 flex items-center gap-2"><FileText size={15} /> No drawing attached</span>
                      )}
                      <a href={`https://wa.me/91${active.phone.replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-5 h-11 border-2 border-charcoal font-mono2 text-xs font-700 uppercase tracking-wider hover:border-safety transition-colors">
                        <Phone size={15} /> WhatsApp Client
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-16 text-charcoal/40">
                    <Inbox size={36} className="mb-3" />
                    <p className="font-mono2 text-sm">Select a request to view details</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  if (!token) return <LoginView onLogin={setToken} />;
  return <InboxView token={token} onLogout={logout} />;
}
